import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, ChevronDown } from 'lucide-react';

// Individual Block Renderers
const HeroBlock = ({ content }) => {
  const { title, subtitle, buttonText, buttonLink, backgroundImage } = content;
  
  return (
    <section 
      className="relative py-24 px-6 overflow-hidden"
      style={backgroundImage ? {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}}
    >
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-6"
        >
          {title}
        </motion.h1>
        {subtitle && (
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto"
          >
            {subtitle}
          </motion.p>
        )}
        {buttonText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to={buttonLink || '#'}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg transition-all hover:scale-105 shadow-glow"
            >
              {buttonText}
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};

const TextBlock = ({ content }) => {
  const { content: htmlContent, alignment = 'left' } = content;
  
  return (
    <section className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`prose prose-lg prose-invert max-w-none text-${alignment}`}
          style={{ textAlign: alignment }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>
    </section>
  );
};

const ImageBlock = ({ content }) => {
  const { src, alt, caption, size = 'full' } = content;
  
  if (!src) return null;
  
  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    full: 'max-w-4xl'
  };
  
  return (
    <section className="py-12 px-6">
      <motion.figure 
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className={`${sizeClasses[size] || sizeClasses.full} mx-auto`}
      >
        <img loading="lazy" decoding="async" 
          src={src} 
          alt={alt || ''} 
          className="w-full rounded-2xl shadow-xl"
        />
        {caption && (
          <figcaption className="text-center text-muted-foreground mt-4 text-sm">
            {caption}
          </figcaption>
        )}
      </motion.figure>
    </section>
  );
};

const GalleryBlock = ({ content }) => {
  const { images = [], columns = 3 } = content;
  
  if (images.length === 0) return null;
  
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4'
  };
  
  return (
    <section className="py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className={`grid ${gridCols[columns] || gridCols[3]} gap-4`}>
          {images.map((img, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="aspect-square overflow-hidden rounded-xl"
            >
              <img loading="lazy" decoding="async" 
                src={img.src} 
                alt={img.alt || ''} 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTABlock = ({ content }) => {
  const { title, description, buttonText, buttonLink, style = 'primary' } = content;
  
  return (
    <section className="py-16 px-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto text-center bg-card border border-border/50 rounded-3xl p-12"
      >
        <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-4">
          {title}
        </h2>
        {description && (
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {description}
          </p>
        )}
        {buttonText && (
          <Link
            to={buttonLink || '#'}
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 ${
              style === 'primary' 
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-glow'
                : 'bg-card border-2 border-primary text-primary hover:bg-primary/10'
            }`}
          >
            {buttonText}
          </Link>
        )}
      </motion.div>
    </section>
  );
};

const FAQBlock = ({ content }) => {
  const { title, items = [] } = content;
  const [openIndex, setOpenIndex] = React.useState(null);
  
  if (items.length === 0) return null;
  
  return (
    <section className="py-16 px-6">
      <div className="max-w-3xl mx-auto">
        {title && (
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold font-display text-foreground text-center mb-12"
          >
            {title}
          </motion.h2>
        )}
        <div className="space-y-4">
          {items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-card border border-border/50 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-background/50 transition-colors"
              >
                <span className="font-bold text-foreground pr-4">{item.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-primary flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6 text-muted-foreground">
                  {item.answer}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const QuoteBlock = ({ content }) => {
  const { text, author, role } = content;
  
  return (
    <section className="py-16 px-6">
      <motion.blockquote 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto text-center"
      >
        <p className="text-2xl md:text-3xl font-display text-foreground italic mb-6">
          "{text}"
        </p>
        {author && (
          <footer className="text-muted-foreground">
            <cite className="not-italic">
              <span className="font-bold text-foreground">{author}</span>
              {role && <span className="text-muted-foreground"> — {role}</span>}
            </cite>
          </footer>
        )}
      </motion.blockquote>
    </section>
  );
};

const VideoBlock = ({ content }) => {
  const { url, title } = content;
  
  if (!url) return null;
  
  // Extract video ID from YouTube or Vimeo URLs
  let embedUrl = '';
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    const videoId = url.includes('youtu.be') 
      ? url.split('/').pop()
      : new URLSearchParams(new URL(url).search).get('v');
    embedUrl = `https://www.youtube.com/embed/${videoId}`;
  } else if (url.includes('vimeo.com')) {
    const videoId = url.split('/').pop();
    embedUrl = `https://player.vimeo.com/video/${videoId}`;
  }
  
  if (!embedUrl) return null;
  
  return (
    <section className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {title && (
          <h3 className="text-2xl font-bold font-display text-foreground text-center mb-6">
            {title}
          </h3>
        )}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="aspect-video rounded-2xl overflow-hidden shadow-xl"
        >
          <iframe
            src={embedUrl}
            title={title || 'Video'}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </motion.div>
      </div>
    </section>
  );
};

const ContactBlock = ({ content }) => {
  const { email, phone, address } = content;
  
  return (
    <section className="py-16 px-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl mx-auto bg-card border border-border/50 rounded-2xl p-8"
      >
        <h3 className="text-2xl font-bold font-display text-foreground text-center mb-8">
          Contact Us
        </h3>
        <div className="space-y-4">
          {email && (
            <a 
              href={`mailto:${email}`}
              className="flex items-center gap-4 p-4 bg-background rounded-xl hover:bg-primary/10 transition-colors"
            >
              <Mail className="w-6 h-6 text-primary" />
              <span className="text-foreground">{email}</span>
            </a>
          )}
          {phone && (
            <a 
              href={`tel:${phone}`}
              className="flex items-center gap-4 p-4 bg-background rounded-xl hover:bg-primary/10 transition-colors"
            >
              <Phone className="w-6 h-6 text-primary" />
              <span className="text-foreground">{phone}</span>
            </a>
          )}
          {address && (
            <div className="flex items-center gap-4 p-4 bg-background rounded-xl">
              <MapPin className="w-6 h-6 text-primary flex-shrink-0" />
              <span className="text-foreground">{address}</span>
            </div>
          )}
        </div>
      </motion.div>
    </section>
  );
};

const SpacerBlock = ({ content }) => {
  const { height = 60 } = content;
  return <div style={{ height: `${height}px` }} />;
};

// Block Type Mapping
const BLOCK_RENDERERS = {
  hero: HeroBlock,
  text: TextBlock,
  image: ImageBlock,
  gallery: GalleryBlock,
  cta: CTABlock,
  faq: FAQBlock,
  quote: QuoteBlock,
  video: VideoBlock,
  contact: ContactBlock,
  spacer: SpacerBlock,
};

// Main Page Renderer Component
export const PageRenderer = ({ blocks = [] }) => {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <div className="page-content">
      {blocks.map((block, index) => {
        const BlockComponent = BLOCK_RENDERERS[block.type];
        
        if (!BlockComponent) {
          console.warn(`Unknown block type: ${block.type}`);
          return null;
        }
        
        return (
          <BlockComponent 
            key={block.id || index} 
            content={block.content || {}} 
          />
        );
      })}
    </div>
  );
};

export default PageRenderer;
